import React, { useEffect } from "react";
import { HiringProcessTemplateSelect } from "@/features/hiring-process/hiring-process-template-select";
import {
  JobPostRequirementsEditor,
  RequirementRow,
} from "@/features/job-post/components/job-post-requirements-editor";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X, Loader2, AlertCircle, Info } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// shadcn components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TextEditor } from "@/components/TextEditor";
import {
  JobPostSchemaType,
  MatchStrictness,
  jobPostSchema,
  makeRequirementKey,
} from "../utils/job-data";
import { SpecialtiesSelectObject } from "@/components/Selects/SpecialtiesSelectObject";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FacilityEditDialog } from "@/features/facility/facility-edit-dialog";
import { FacilitySelect } from "@/components/Selects/facility-select";
import { CitySelect } from "@/components/Selects/CitySelect";
import {
  JobPostRequirementOperator,
  JobPostRequirementSeverity,
  JobPostRequirementType,
  JobPostVisibility,
  PracticeSetting,
  WageFrequency,
} from "@api/shared-runtime/enums";
import { EmploymentTypeSelect } from "@/components/Selects/EmploymentTypeSelect";
import { PracticeSettingSelect } from "@/components/Selects/PracticeSettingSelect";
import { useAIFormAssistant } from "@/hooks/use-ai-form-assistant";
import { AIAssistantCard } from "@/components/ai-assistant/ai-assistant-card";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useOrganization } from "@/hooks/use-organization";

// ─── Match-Strictness Row ─────────────────────────────────────────────────────

/**
 * Three-option radio row rendered below Professional Types or Specialties.
 * When the parent multi-select has no values, the control is disabled and
 * auto-resets to "none".
 */
// ─── AI requirement-row conversion ───────────────────────────────────────────
// The AI emits a minimal shape; the editor needs full RequirementRow objects.
// These helpers translate at apply time inside `transformFormState`.

function isFullRequirementRow(row: unknown): row is RequirementRow {
  return (
    typeof row === "object" &&
    row !== null &&
    "_key" in row &&
    typeof (row as { _key: unknown })._key === "string"
  );
}

// Returns null when the row doesn't match a known AI shape — caller filters
// these out. The AI prompt restricts emitted rows to `free_text` and
// `state_license`; any other shape is dropped rather than coerced into a
// state-license row (which would crash on missing `states`).
function aiRowToRequirementRow(
  row: unknown,
  existing: RequirementRow[],
): RequirementRow | null {
  if (typeof row !== "object" || row === null || !("type" in row)) {
    return null;
  }
  const typed = row as { type: unknown };
  if (typed.type === "free_text") {
    const text = (row as { text?: unknown }).text;
    if (typeof text !== "string") {
      return null;
    }
    // Reuse `_key` for an existing free-text row with the same text so React
    // doesn't unmount the editor input mid-keystroke when the AI returns the
    // current text unchanged.
    const match = existing.find(
      (e) =>
        e.type === JobPostRequirementType.FreeText &&
        e.value.kind === "free_text" &&
        e.value.text === text,
    );
    return {
      _key: match?._key ?? makeRequirementKey(),
      type: JobPostRequirementType.FreeText,
      operator: JobPostRequirementOperator.Matches,
      severity: JobPostRequirementSeverity.Flag,
      displayLabel: text,
      value: { kind: "free_text", text },
    };
  }
  if (typed.type === "state_license") {
    const r = row as {
      states?: unknown;
      severity?: unknown;
    };
    if (
      !Array.isArray(r.states) ||
      !r.states.every((s): s is string => typeof s === "string") ||
      (r.severity !== "block" && r.severity !== "flag")
    ) {
      return null;
    }
    const severity =
      r.severity === "block"
        ? JobPostRequirementSeverity.Block
        : JobPostRequirementSeverity.Flag;
    const sortedStates = r.states.map((s) => s.toUpperCase()).sort();
    const match = existing.find(
      (e) =>
        e.type === JobPostRequirementType.StateLicense &&
        e.value.kind === "state_license" &&
        e.severity === severity &&
        [...e.value.states]
          .map((s) => s.toUpperCase())
          .sort()
          .join(",") === sortedStates.join(","),
    );
    return {
      _key: match?._key ?? makeRequirementKey(),
      type: JobPostRequirementType.StateLicense,
      operator: JobPostRequirementOperator.HasAny,
      severity,
      displayLabel: "",
      value: { kind: "state_license", states: sortedStates },
    };
  }
  return null;
}

function MatchStrictnessRow({
  label,
  disabled,
  value,
  onChange,
}: {
  label: string;
  disabled: boolean;
  value: MatchStrictness;
  onChange: (v: MatchStrictness) => void;
}) {
  const options: {
    value: MatchStrictness;
    label: string;
    description: string;
  }[] = [
    {
      value: "none",
      label: "Don't enforce",
      description: "Used for AI matching only.",
    },
    {
      value: "flag",
      label: "Flag for review",
      description: "Org sees a flag; provider proceeds.",
    },
    {
      value: "block",
      label: "Block applications",
      description: "Provider cannot apply without matching.",
    },
  ];

  // If the parent field is cleared, silently reset to "none"
  React.useEffect(() => {
    if (disabled && value !== "none") {
      onChange("none");
    }
  }, [disabled, value, onChange]);

  return (
    <div
      className={`rounded-xl border p-4 space-y-3 ${disabled ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-medium text-slate-700">
          {label}
          {disabled && (
            <span className="ml-1.5 text-slate-400 font-normal">
              (select values above to enable)
            </span>
          )}
        </p>
        <Popover>
          <PopoverTrigger
            type="button"
            aria-label="How enforcement works"
            className="shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Info className="h-3.5 w-3.5" />
          </PopoverTrigger>
          <PopoverContent className="w-80 max-w-[calc(100vw-2rem)] rounded-xl border-slate-200 p-4 text-xs text-slate-700 space-y-2">
            <p className="font-medium text-slate-900">How enforcement works</p>
            <p>
              When a provider clicks "interested" on this job, the platform
              checks the values you've selected above against their profile.
              Enforcement controls what happens if they don't match:
            </p>
            <ul className="space-y-1.5 pl-1">
              <li>
                <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                  <span className="inline-block w-2 h-2 rounded-sm bg-slate-300" />
                  Don't enforce
                </span>
                {" — "}
                The values are still used by AI matching and shown on the
                listing, but providers can apply regardless.
              </li>
              <li>
                <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                  <span className="inline-block w-2 h-2 rounded-sm bg-amber-300" />
                  Flag for review
                </span>
                {" — "}
                The provider proceeds normally, but your candidate list shows a
                "Needs review" badge so you know to double-check before
                advancing them.
              </li>
              <li>
                <span className="inline-flex items-center gap-1 font-medium text-red-700">
                  <span className="inline-block w-2 h-2 rounded-sm bg-red-300" />
                  Block applications
                </span>
                {" — "}
                The provider cannot express interest. They see a message
                explaining what's missing and a link to update their
                credentials.
              </li>
            </ul>
            <p className="text-slate-500 pt-1">
              Changing enforcement only affects new applicants — existing
              candidates aren't re-evaluated.
            </p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            title={opt.description}
            aria-pressed={value === opt.value}
            className={`min-h-12 rounded-xl border px-3 py-2 text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 ${
              value === opt.value
                ? opt.value === "block"
                  ? "border-red-300 bg-red-50 text-red-700 font-medium"
                  : opt.value === "flag"
                    ? "border-amber-300 bg-amber-50 text-amber-700 font-medium"
                    : "border-primary bg-primary/5 text-primary font-medium"
                : "border-slate-400 bg-white text-slate-700 hover:border-slate-500 hover:bg-slate-50"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {value !== "none" && !disabled && (
        <p className="text-xs text-slate-500">
          {options.find((o) => o.value === value)?.description}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const collectErrorMessages = (value: unknown): string[] => {
  if (!value || typeof value !== "object") {
    return [];
  }

  const maybeFieldError = value as { message?: unknown };
  const messages =
    typeof maybeFieldError.message === "string"
      ? [maybeFieldError.message]
      : [];

  return [
    ...messages,
    ...Object.values(value).flatMap((child) => collectErrorMessages(child)),
  ];
};

export function JobPostForm({
  onSave,
  defaultValues,
  onCancel,
  onChange,
  jobPostId,
  requirementsLoading,
  saveError,
}: {
  onSave: (params: {
    data: JobPostSchemaType;
    isDraft: boolean;
  }) => void | Promise<void>;
  defaultValues?: Partial<JobPostSchemaType>;
  onCancel: () => void;
  onChange?: (params: { data: JobPostSchemaType; isDraft: boolean }) => void;
  jobPostId?: string;
  /** When true the requirements editor shows a loading state (parent is fetching) */
  requirementsLoading?: boolean;
  /** Server-side save error to show near the action buttons. */
  saveError?: string | null;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const responsibilityInputId = React.useId();
  const preferredQualificationInputId = React.useId();
  const requireHiringProcessSelection = Boolean(
    selectedOrgBusinessUnit.enableHiringProcess,
  );
  const form = useForm<JobPostSchemaType>({
    resolver: zodResolver(jobPostSchema),
    defaultValues: {
      ...defaultValues,
      requireHiringProcessSelection,
    },
  });

  const trpc = useTRPC();
  const { data: availableHealthcareProfessionalTypes } = useQuery(
    trpc.healthcareProfessionalTypes.getAll.queryOptions(),
  );
  const { data: availableSpecialties } = useQuery(
    trpc.healthcareProfessionalTypes.getMedicalSpecialties.queryOptions(),
  );

  const aiAssistant = useAIFormAssistant({
    formType: "jobPost",
    form,
    mergeFormState: true,
    additionalContext: {
      availableHealthcareProfessionalTypes:
        availableHealthcareProfessionalTypes?.map((hpt) => ({
          id: hpt.id,
          name: hpt.name,
        })) ?? [],
      availableSpecialties:
        availableSpecialties?.map((s) => ({ id: s.id, name: s.name })) ?? [],
    },
    transformFormState: (next, current) => {
      // The AI emits a minimal `requirements` shape (no `_key`, narrower
      // value layout). Convert each entry into a full `RequirementRow` and
      // preserve the existing `_key` when an equivalent row already exists
      // (so React list rendering doesn't blow away focus on edit). Rows
      // that don't match a known AI shape (drift, prompt-injected garbage)
      // are dropped rather than coerced.
      const aiRows = next.requirements;
      if (Array.isArray(aiRows)) {
        const existing = (current.requirements ?? []) as RequirementRow[];
        next.requirements = aiRows
          .map((row) =>
            isFullRequirementRow(row)
              ? row
              : aiRowToRequirementRow(row, existing),
          )
          .filter((row): row is RequirementRow => row !== null);
      }
      return next;
    },
  });

  const aiStarterPrompts = [
    "Write a job post for a full-time hospitalist covering internal medicine rounds",
    "Draft a locums ER physician posting with 12-hour shifts",
  ];

  // Subscribe to severity fields via `useWatch` — these need to react to
  // `form.reset` calls from the AI form-assist (which set new severity
  // values atomically with healthcareProfessionalTypes / specialties).
  // `form.watch(name)` inside a nested FormField's render prop doesn't
  // reliably subscribe across reset cycles; `useWatch` registers a hook
  // subscription that fires whenever the named field changes regardless
  // of where the form-reset originated.
  const professionalTypeSeverity = (useWatch({
    control: form.control,
    name: "professionalTypeSeverity",
  }) ?? "none") as MatchStrictness;
  const specialtySeverity = (useWatch({
    control: form.control,
    name: "specialtySeverity",
  }) ?? "none") as MatchStrictness;
  // Also subscribe to the entity arrays so the disabled state of the
  // MatchStrictnessRow updates correctly after AI reset (Controller's
  // own field.value renders the chips, but the `disabled` prop computed
  // here needs an independent subscription too).
  const watchedProfTypes = useWatch({
    control: form.control,
    name: "healthcareProfessionalTypes",
  });
  const watchedSpecialties = useWatch({
    control: form.control,
    name: "specialties",
  });

  // Watch for form changes and call onChange if provided
  useEffect(() => {
    form.setValue(
      "requireHiringProcessSelection",
      requireHiringProcessSelection,
    );
  }, [form, requireHiringProcessSelection]);

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (onChange) {
        onChange({ data: value as JobPostSchemaType, isDraft: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onChange]);

  // Clear fields when location type changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "locationType") {
        const locationType = value.locationType;

        // Clear all location-related fields first
        if (locationType === "facility") {
          // Clear city/state and practice setting when switching to facility
          form.setValue("city", null);
          form.setValue("stateCode", null);
          form.setValue("remote", null);
          form.setValue("practiceSetting", null, {
            shouldDirty: true,
            shouldValidate: true,
          });
          form.clearErrors("practiceSetting");
        } else if (locationType === "city-state") {
          // Clear facility when switching to city/state
          form.setValue("facility", null);
          form.setValue("remote", false);
          form.clearErrors("practiceSetting");
        } else if (locationType === "remote") {
          // Clear both facility and city/state when switching to remote
          form.setValue("facility", null);
          form.setValue("city", null);
          form.setValue("stateCode", null);
          form.setValue("remote", true);
          form.setValue("practiceSetting", PracticeSetting.RemoteVirtual, {
            shouldDirty: true,
            shouldValidate: true,
          });
          form.clearErrors("practiceSetting");
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: JobPostSchemaType, draft: boolean) => {
    setIsSaving(true);
    try {
      await onSave({ data, isDraft: draft });
    } catch (error) {
      // Don't swallow — the parent component owns the error message and
      // already renders an alert. Log for the console and keep state
      // consistent so the user can re-submit after fixing the issue.
      console.error("Error saving job posting:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFacilitySuccess = (facility: {
    id: string;
    name: string;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    isRemote: boolean | null;
  }) => {
    form.setValue("facility", facility);
  };

  const [isSaving, setIsSaving] = React.useState(false);
  const [isFacilityDialogOpen, setIsFacilityDialogOpen] = React.useState(false);
  const [newResponsibility, setNewResponsibility] = React.useState("");
  const [newPreferredQualification, setNewPreferredQualification] =
    React.useState("");
  const validationErrorMessages = Array.from(
    new Set(collectErrorMessages(form.formState.errors)),
  );

  const addResponsibility = () => {
    if (newResponsibility.trim()) {
      const currentResponsibilities = form.getValues("responsibilities") || [];
      form.setValue("responsibilities", [
        ...currentResponsibilities,
        newResponsibility,
      ]);
      setNewResponsibility("");
    }
  };

  const removeResponsibility = (index: number) => {
    const currentResponsibilities = form.getValues("responsibilities") || [];
    form.setValue(
      "responsibilities",
      currentResponsibilities.filter((_, i) => i !== index),
    );
  };

  const addPreferredQualification = () => {
    if (newPreferredQualification.trim()) {
      const currentPreferredQualifications =
        form.getValues("preferredQualifications") || [];
      form.setValue("preferredQualifications", [
        ...currentPreferredQualifications,
        newPreferredQualification,
      ]);
      setNewPreferredQualification("");
    }
  };

  const removePreferredQualification = (index: number) => {
    const currentPreferredQualifications =
      form.getValues("preferredQualifications") || [];
    form.setValue(
      "preferredQualifications",
      currentPreferredQualifications.filter((_, i) => i !== index),
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => onSubmit(data, false))}
        className="space-y-6"
      >
        {/* AI Assistant — front and center */}
        <AIAssistantCard
          messages={aiAssistant.messages}
          onSendMessage={aiAssistant.sendMessage}
          isLoading={aiAssistant.isLoading}
          onClearConversation={aiAssistant.clearConversation}
          title="Draft your job post with AI"
          emptyStatePrompt="Describe the role you're hiring for and I'll draft the title, description, responsibilities, qualifications, and schedule. Nothing saves until you click Post Job or Save Draft."
          inputPlaceholder="e.g. Describe the role you want to post..."
          starterPrompts={aiStarterPrompts}
          pendingAttachments={aiAssistant.pendingAttachments}
          onAddAttachment={aiAssistant.addAttachment}
          onRemoveAttachment={aiAssistant.removeAttachment}
          hasInvalidAttachment={aiAssistant.hasInvalidAttachment}
        />

        <Card className="rounded-2xl border-slate-200 bg-white shadow-xs">
          <CardHeader className="rounded-t-2xl border-b border-slate-200 bg-slate-50/60 px-5 py-5 sm:px-6">
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-5 sm:p-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="font-medium">Position Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Urgent Care Physician"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="externalId"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="font-medium">
                    Client/External Job ID{" "}
                    <span className="text-slate-500">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter the client/internal job reference (optional)"
                      {...field}
                    />
                  </FormControl>
                  <p className="text-xs text-slate-500">
                    Helpful for tracking this posting against the client or ATS
                    reference.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <HiringProcessTemplateSelect
              value={form.watch("hiringProcessId")}
              error={form.formState.errors.hiringProcessId?.message}
              onChange={(templateId) =>
                form.setValue("hiringProcessId", templateId, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="font-medium">
                    Provider Visibility
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value ?? JobPostVisibility.Public}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select visibility" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={JobPostVisibility.Public}>
                        Visible to everyone
                      </SelectItem>
                      <SelectItem value={JobPostVisibility.DirectLink}>
                        Only people with a link
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Link-only jobs are hidden from provider search and
                    recommendations after publishing, but providers with the
                    direct link can still view them.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="healthcareProfessionalTypes"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="font-medium">
                      Healthcare Professional Type
                    </FormLabel>
                    <HealthcareProfessionalTypeSelect
                      isMulti={true}
                      onChange={field.onChange}
                      value={field.value}
                    />
                    <MatchStrictnessRow
                      label="Enforcement"
                      // Read disabled from useWatch (above) instead of
                      // Controller's field.value. The two subscribers can
                      // update on different render commits after form.reset
                      // — using the same source ensures `disabled` and
                      // `value` agree, so the auto-revert useEffect doesn't
                      // fire on an inconsistent transient state.
                      disabled={(watchedProfTypes ?? []).length === 0}
                      value={professionalTypeSeverity}
                      onChange={(v) =>
                        form.setValue("professionalTypeSeverity", v, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="specialties"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="font-medium">Specialties</FormLabel>
                    <SpecialtiesSelectObject
                      isMulti={true}
                      onChange={field.onChange}
                      value={field.value}
                    />
                    <MatchStrictnessRow
                      label="Enforcement"
                      disabled={(watchedSpecialties ?? []).length === 0}
                      value={specialtySeverity}
                      onChange={(v) =>
                        form.setValue("specialtySeverity", v, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Location Selection */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="locationType"
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel className="font-medium">Location Type</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2"
                      >
                        <Label
                          htmlFor="facility"
                          className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors hover:bg-slate-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/20 ${
                            field.value === "facility"
                              ? "border-primary bg-primary/5 text-primary"
                              : "border-slate-400 bg-white"
                          }`}
                        >
                          <RadioGroupItem value="facility" id="facility" />
                          <span className="font-normal">Specific Facility</span>
                        </Label>
                        <Label
                          htmlFor="city-state"
                          className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors hover:bg-slate-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/20 ${
                            field.value === "city-state"
                              ? "border-primary bg-primary/5 text-primary"
                              : "border-slate-400 bg-white"
                          }`}
                        >
                          <RadioGroupItem value="city-state" id="city-state" />
                          <span className="font-normal">City & State</span>
                        </Label>
                        <Label
                          htmlFor="remote"
                          className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors hover:bg-slate-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/20 ${
                            field.value === "remote"
                              ? "border-primary bg-primary/5 text-primary"
                              : "border-slate-400 bg-white"
                          }`}
                        >
                          <RadioGroupItem value="remote" id="remote" />
                          <span className="font-normal">Remote</span>
                        </Label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Facility Selection - shown when facility is selected */}
              {form.watch("locationType") === "facility" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="facility"
                    render={({ field }) => (
                      <FormItem className="min-w-0">
                        <FormLabel className="font-medium">
                          Facility <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl className="min-w-0 flex-1">
                          <FacilitySelect
                            value={field.value || undefined}
                            onChange={field.onChange}
                            placeholder="Select facility"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => setIsFacilityDialogOpen(true)}
                      className="w-full gap-2 md:w-auto"
                      aria-label="Add new facility"
                    >
                      <Plus className="h-4 w-4" /> Add New
                    </Button>
                  </div>
                </div>
              )}

              {/* City & State Selection - shown when city-state is selected */}
              {form.watch("locationType") === "city-state" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="cityLocation"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel className="font-medium">
                          City <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <CitySelect
                            value={field.value ? [field.value] : []}
                            onChange={(cities) =>
                              field.onChange(cities[0] ?? null)
                            }
                            multiple={false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="practiceSetting"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel className="font-medium">
                          Practice Setting{" "}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <PracticeSettingSelect
                            value={field.value ? [field.value] : []}
                            onChange={(values) =>
                              field.onChange(values[0] ?? null)
                            }
                            placeholder="Select practice setting"
                            isMulti={false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="wageMinAmount"
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel className="font-medium">Wage Min</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                          $
                        </span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          {...field}
                          className="pl-7"
                          placeholder="100"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="wageMaxAmount"
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel className="font-medium">Wage Max</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                          $
                        </span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          {...field}
                          className="pl-7"
                          placeholder="200"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="wageFrequencyId"
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel className="font-medium">
                      Wage Frequency
                    </FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value as WageFrequency)
                      }
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select wage frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="hourly">Per Hour</SelectItem>
                        <SelectItem value="yearly">Per Year</SelectItem>
                        <SelectItem value="one_time">Per Shift</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {form.watch("wageFrequencyId") === WageFrequency.Hourly && (
              <>
                <FormField
                  control={form.control}
                  name="guaranteedBasePay"
                  render={({ field }) => (
                    <FormItem
                      className={`flex min-h-12 cursor-pointer flex-row items-center justify-between gap-3 rounded-xl border p-4 transition-colors hover:bg-slate-50 ${
                        field.value
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-slate-400 bg-white"
                      }`}
                      onClick={() => field.onChange(!field.value)}
                    >
                      <div className="space-y-0.5">
                        <FormLabel className="font-medium cursor-pointer">
                          Guaranteed Base
                        </FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {form.watch("guaranteedBasePay") && (
                  <FormField
                    control={form.control}
                    name="guaranteedBasePayDescription"
                    render={({ field }) => (
                      <FormItem className="min-w-0">
                        <FormLabel className="font-medium">
                          Guaranteed Base Details (Optional)
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Provide additional details about the guaranteed base salary..."
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-200 bg-white shadow-xs">
          <CardHeader className="rounded-t-2xl border-b border-slate-200 bg-slate-50/60 px-5 py-5 sm:px-6">
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-5 sm:p-6">
            <FormField
              control={form.control}
              name="employmentTypeIds"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="font-medium">Employment Type</FormLabel>
                  <FormControl>
                    <EmploymentTypeSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select employment types"
                      isMulti={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="min-w-0 space-y-4">
                <FormField
                  control={form.control}
                  name="startDateType"
                  render={({ field }) => (
                    <FormItem className="min-w-0">
                      <FormLabel className="font-medium">Start Date</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select start date option" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="immediate">
                              Start immediately (after credentialing)
                            </SelectItem>
                            <SelectItem value="specific">
                              Specify a start date
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch("startDateType") === "specific" && (
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem className="min-w-0 space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                        <FormLabel className="font-medium">
                          Select Start Date
                        </FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input
                              type="date"
                              value={field.value ?? ""}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              <div className="min-w-0 space-y-4">
                <FormField
                  control={form.control}
                  name="endDateType"
                  render={({ field }) => (
                    <FormItem className="min-w-0">
                      <FormLabel className="font-medium">End Date</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select end date option" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">No end date</SelectItem>
                            <SelectItem value="specific">
                              Specify an end date
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch("endDateType") === "specific" && (
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem className="min-w-0 space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                        <FormLabel className="font-medium">
                          Select End Date
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            value={field.value ?? ""}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
            <Separator className="my-6" />
            <FormField
              control={form.control}
              name="scheduleDescription"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="font-medium">
                    Schedule Description
                  </FormLabel>
                  <FormControl>
                    <TextEditor
                      contentHTML={field.value || ""}
                      placeholder="Describe the schedule in detail. This will help detail if it's different per week."
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-xs">
          <CardHeader className="rounded-t-2xl border-b border-slate-200 bg-slate-50/60 px-5 py-5 sm:px-6">
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">
              Requirements
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              All job requirements live here. Free-text rows are descriptive;
              structured rows (state license, etc.) are enforced as automatic
              gates.
            </p>
          </CardHeader>
          <CardContent className="space-y-6 p-5 sm:p-6">
            <div>
              <Label htmlFor={responsibilityInputId} className="font-medium">
                Responsibilities
              </Label>
              <div className="mt-2 flex gap-2">
                <Input
                  id={responsibilityInputId}
                  value={newResponsibility}
                  onChange={(e) => setNewResponsibility(e.target.value)}
                  placeholder="Add a responsibility and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addResponsibility();
                    }
                  }}
                  className="min-w-0 flex-1"
                />
                <Button
                  type="button"
                  onClick={addResponsibility}
                  aria-label="Add responsibility"
                  className="size-12 shrink-0 rounded-xl"
                  variant="default"
                  size="icon"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <FormMessage>
                {form.formState.errors.responsibilities?.message}
              </FormMessage>
              <ScrollArea className="mt-3">
                <div className="space-y-2">
                  {form.watch("responsibilities")?.map((resp, index) => (
                    <div key={index} className="flex items-center group">
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                        <span className="min-w-0 flex-1 break-words py-2 text-sm leading-relaxed text-slate-700">
                          {resp}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="size-10 shrink-0 rounded-xl p-0 text-slate-500 hover:bg-red-50 hover:text-red-700"
                          onClick={() => removeResponsibility(index)}
                          aria-label={`Remove responsibility ${index + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <Separator className="my-6" />

            {/* Unified requirements editor — replaces the old free-text "Required Qualifications" list */}
            <JobPostRequirementsEditor
              value={form.watch("requirements") ?? []}
              onChange={(rows: RequirementRow[]) =>
                form.setValue("requirements", rows, { shouldDirty: true })
              }
              jobPostId={jobPostId}
              organizationBusinessUnitId={
                defaultValues?.organizationBusinessUnitId ??
                selectedOrgBusinessUnit.id
              }
              isLoading={requirementsLoading}
            />

            <Separator className="my-6" />

            <div>
              <Label
                htmlFor={preferredQualificationInputId}
                className="font-medium"
              >
                Preferred Qualifications
              </Label>
              <div className="mt-2 flex gap-2">
                <Input
                  id={preferredQualificationInputId}
                  value={newPreferredQualification}
                  onChange={(e) => setNewPreferredQualification(e.target.value)}
                  placeholder="Add a qualification and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addPreferredQualification();
                    }
                  }}
                  className="min-w-0 flex-1"
                />
                <Button
                  type="button"
                  onClick={addPreferredQualification}
                  aria-label="Add preferred qualification"
                  className="size-12 shrink-0 rounded-xl"
                  variant="default"
                  size="icon"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <FormMessage>
                {form.formState.errors.preferredQualifications?.message}
              </FormMessage>
              <div
                role="region"
                aria-label="Preferred qualifications list"
                tabIndex={0}
                className="mt-3 max-h-[200px] overflow-x-hidden overflow-y-auto rounded-xl [scrollbar-gutter:stable] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <div className="space-y-2">
                  {form.watch("preferredQualifications")?.map((qual, index) => (
                    <div key={index} className="flex items-center group">
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                        <span className="min-w-0 flex-1 break-words py-2 text-sm leading-relaxed text-slate-700">
                          {qual}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="size-10 shrink-0 rounded-xl p-0 text-slate-500 hover:bg-red-50 hover:text-red-700"
                          onClick={() => removePreferredQualification(index)}
                          aria-label={`Remove preferred qualification ${index + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-xs">
          <CardHeader className="rounded-t-2xl border-b border-slate-200 bg-slate-50/60 px-5 py-5 sm:px-6">
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">
              Job Description
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-5 sm:p-6">
            <FormField
              control={form.control}
              name="jobDescription"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormControl>
                    <TextEditor
                      contentHTML={field.value || ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-xs">
          <CardHeader className="rounded-t-2xl border-b border-slate-200 bg-slate-50/60 px-5 py-5 sm:px-6">
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">
              Organization Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-5 sm:p-6">
            <FormField
              control={form.control}
              name="organizationNotes"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="font-medium">Internal Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add internal notes about this job posting (only visible to your organization)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-xs">
          <CardContent className="p-5 sm:p-6">
            {validationErrorMessages.length > 0 ? (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Fix the following before continuing</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-5">
                    {validationErrorMessages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
            {saveError ? (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unable to save job post</AlertTitle>
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {!defaultValues?.publishedAt && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onSubmit(form.getValues(), true)}
                  className="w-full sm:w-auto"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Draft"
                  )}
                </Button>
              )}
              <Button
                type="submit"
                disabled={isSaving}
                className="w-full font-semibold sm:w-auto"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Saving...
                  </>
                ) : defaultValues?.id && defaultValues?.publishedAt ? (
                  "Update Job"
                ) : (
                  "Post Job"
                )}
              </Button>

              <Button
                variant="outline"
                type="button"
                className="w-full sm:w-auto"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
      <FacilityEditDialog
        facility={null}
        isOpen={isFacilityDialogOpen}
        onOpenChange={setIsFacilityDialogOpen}
        onSuccess={handleFacilitySuccess}
      />
    </Form>
  );
}
